/**
 * aeterna/neural/math.ts — The Substrate
 *
 * Pure Float32Array math primitives shared by every neural module.
 * Zero dependencies. Zero allocations in hot paths where possible.
 * These are the atoms from which felt intelligence is built.
 *
 * Every function here operates on typed arrays for cache locality
 * and SIMD-friendly access patterns. No objects, no classes, no
 * garbage collection pressure in the tight loops.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

export const TAU = Math.PI * 2;
export const LN2 = Math.LN2;
export const SQRT2 = Math.SQRT2;

// ─── Deterministic Seeded PRNG ───────────────────────────────────────────────
// Mulberry32 — fast, simple, deterministic. Used for reproducible weight init.

export function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Gaussian random via Box-Muller using seeded PRNG */
export function gaussianSeeded(rng: () => number): number {
  const u1 = rng();
  const u2 = rng();
  return Math.sqrt(-2 * Math.log(u1 + 1e-10)) * Math.cos(TAU * u2);
}

/** Standard Gaussian random (non-seeded, for convenience) */
export function gaussian(): number {
  const u1 = Math.random();
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1 + 1e-10)) * Math.cos(TAU * u2);
}

// ─── Vector Operations ───────────────────────────────────────────────────────

/** Allocate a zero-filled Float32Array */
export function zeros(n: number): Float32Array {
  return new Float32Array(n);
}

/** Allocate Float32Array filled with value */
export function fill(n: number, val: number): Float32Array {
  const out = new Float32Array(n);
  out.fill(val);
  return out;
}

/** Dot product */
export function dot(a: Float32Array, b: Float32Array): number {
  let sum = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) sum += a[i] * b[i];
  return sum;
}

/** Element-wise addition: out = a + b (allocates if out not provided) */
export function vadd(a: Float32Array, b: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] + b[i];
  return r;
}

/** Element-wise subtraction: out = a - b */
export function vsub(a: Float32Array, b: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] - b[i];
  return r;
}

/** Element-wise multiplication: out = a * b */
export function vmul(a: Float32Array, b: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] * b[i];
  return r;
}

/** Scalar multiply: out = a * s */
export function vscale(a: Float32Array, s: number, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] * s;
  return r;
}

/** Scalar add: out = a + s */
export function vshift(a: Float32Array, s: number, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] + s;
  return r;
}

/** L2 norm */
export function norm(a: Float32Array): number {
  return Math.sqrt(dot(a, a));
}

/** L2 normalize in-place, returns same array */
export function normalize(a: Float32Array): Float32Array {
  const n = norm(a);
  if (n > 1e-10) {
    const inv = 1 / n;
    for (let i = 0; i < a.length; i++) a[i] *= inv;
  }
  return a;
}

/** Cosine similarity */
export function cosine(a: Float32Array, b: Float32Array): number {
  const d = dot(a, b);
  const na = norm(a);
  const nb = norm(b);
  if (na < 1e-10 || nb < 1e-10) return 0;
  return d / (na * nb);
}

/** Clamp each element */
export function vclamp(a: Float32Array, lo: number, hi: number, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = Math.max(lo, Math.min(hi, a[i]));
  return r;
}

// ─── Activation Functions ────────────────────────────────────────────────────

/** Element-wise tanh */
export function vtanh(a: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = Math.tanh(a[i]);
  return r;
}

/** Element-wise sigmoid */
export function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

export function vsigmoid(a: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = sigmoid(a[i]);
  return r;
}

/** Element-wise ReLU */
export function vrelu(a: Float32Array, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] > 0 ? a[i] : 0;
  return r;
}

/** Leaky ReLU */
export function vleaky(a: Float32Array, alpha = 0.01, out?: Float32Array): Float32Array {
  const n = a.length;
  const r = out ?? new Float32Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] > 0 ? a[i] : alpha * a[i];
  return r;
}

// ─── Softmax ─────────────────────────────────────────────────────────────────

/** Softmax over a Float32Array (numerically stable) */
export function softmax(logits: Float32Array, out?: Float32Array): Float32Array {
  const n = logits.length;
  const r = out ?? new Float32Array(n);
  let max = -Infinity;
  for (let i = 0; i < n; i++) if (logits[i] > max) max = logits[i];
  let sum = 0;
  for (let i = 0; i < n; i++) {
    r[i] = Math.exp(logits[i] - max);
    sum += r[i];
  }
  const inv = 1 / (sum + 1e-10);
  for (let i = 0; i < n; i++) r[i] *= inv;
  return r;
}

/** Log-softmax (more numerically stable for loss) */
export function logSoftmax(logits: Float32Array, out?: Float32Array): Float32Array {
  const n = logits.length;
  const r = out ?? new Float32Array(n);
  let max = -Infinity;
  for (let i = 0; i < n; i++) if (logits[i] > max) max = logits[i];
  let logSumExp = 0;
  for (let i = 0; i < n; i++) logSumExp += Math.exp(logits[i] - max);
  const lse = max + Math.log(logSumExp + 1e-10);
  for (let i = 0; i < n; i++) r[i] = logits[i] - lse;
  return r;
}

// ─── Matrix Operations ───────────────────────────────────────────────────────
// All matrices stored as flat Float32Array in row-major order: M[row * cols + col]

/** Matrix-vector multiply: out = M (rows×cols) × v (cols) → out (rows) */
export function matvec(M: Float32Array, rows: number, cols: number, v: Float32Array, out?: Float32Array): Float32Array {
  const r = out ?? new Float32Array(rows);
  for (let i = 0; i < rows; i++) {
    let sum = 0;
    const offset = i * cols;
    for (let j = 0; j < cols; j++) sum += M[offset + j] * v[j];
    r[i] = sum;
  }
  return r;
}

/** Matrix-matrix multiply: C = A (m×k) × B (k×n) → C (m×n) */
export function matmul(A: Float32Array, m: number, k: number, B: Float32Array, n: number, C?: Float32Array): Float32Array {
  const r = C ?? new Float32Array(m * n);
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < n; j++) {
      let sum = 0;
      for (let p = 0; p < k; p++) sum += A[i * k + p] * B[p * n + j];
      r[i * n + j] = sum;
    }
  }
  return r;
}

/** Outer product: out = a (m) × b^T (n) → out (m×n) */
export function outer(a: Float32Array, b: Float32Array, out?: Float32Array): Float32Array {
  const m = a.length;
  const n = b.length;
  const r = out ?? new Float32Array(m * n);
  for (let i = 0; i < m; i++) {
    const offset = i * n;
    for (let j = 0; j < n; j++) r[offset + j] = a[i] * b[j];
  }
  return r;
}

/** Weighted sum of vectors: out = Σ weights[i] * vectors[i] */
export function weightedSum(vectors: Float32Array[], weights: Float32Array, out?: Float32Array): Float32Array {
  if (vectors.length === 0) return out ?? new Float32Array(0);
  const dim = vectors[0].length;
  const r = out ?? new Float32Array(dim);
  r.fill(0);
  for (let i = 0; i < vectors.length; i++) {
    const w = weights[i];
    const v = vectors[i];
    for (let d = 0; d < dim; d++) r[d] += w * v[d];
  }
  return r;
}

// ─── Random Matrix Init ──────────────────────────────────────────────────────

/** Create a random matrix with Gaussian entries scaled by sigma */
export function randomMatrix(rows: number, cols: number, sigma: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const M = new Float32Array(rows * cols);
  for (let i = 0; i < M.length; i++) M[i] = gaussianSeeded(rng) * sigma;
  return M;
}

/** Sparse random matrix: only ~density fraction of entries are nonzero */
export function sparseRandomMatrix(rows: number, cols: number, sigma: number, density: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const M = new Float32Array(rows * cols);
  for (let i = 0; i < M.length; i++) {
    M[i] = rng() < density ? gaussianSeeded(rng) * sigma : 0;
  }
  return M;
}

/** Random binary vector (Uint8Array, each element 0 or 1) */
export function randomBinary(n: number, seed: number): Uint8Array {
  const rng = mulberry32(seed);
  const v = new Uint8Array(n);
  for (let i = 0; i < n; i++) v[i] = rng() < 0.5 ? 1 : 0;
  return v;
}

/** Spectral radius of a matrix (approximate via power iteration) */
export function spectralRadius(M: Float32Array, n: number, iters = 50): number {
  const rng = mulberry32(42);
  let v = new Float32Array(n);
  let tmp = new Float32Array(n);
  for (let i = 0; i < n; i++) v[i] = gaussianSeeded(rng);
  normalize(v);

  for (let iter = 0; iter < iters; iter++) {
    matvec(M, n, n, v, tmp);
    // Swap buffers (avoids allocation, prevents aliasing corruption)
    const swap = v; v = tmp; tmp = swap;
    normalize(v);
  }
  const Mv = matvec(M, n, n, v);
  return norm(Mv);
}

/** Scale matrix in-place so spectral radius ≈ target */
export function rescaleSpectralRadius(M: Float32Array, n: number, target: number): void {
  const sr = spectralRadius(M, n);
  if (sr > 1e-10) {
    const scale = target / sr;
    for (let i = 0; i < M.length; i++) M[i] *= scale;
  }
}

// ─── One-Hot / Encoding ──────────────────────────────────────────────────────

/** One-hot encode an index into a Float32Array of given size */
export function oneHot(idx: number, size: number, out?: Float32Array): Float32Array {
  const r = out ?? new Float32Array(size);
  r.fill(0);
  if (idx >= 0 && idx < size) r[idx] = 1;
  return r;
}

/** Argmax of a Float32Array */
export function argmax(a: Float32Array): number {
  let best = 0;
  let bestVal = a[0];
  for (let i = 1; i < a.length; i++) {
    if (a[i] > bestVal) { bestVal = a[i]; best = i; }
  }
  return best;
}

/** Sum of all elements */
export function sum(a: Float32Array): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i];
  return s;
}

/** Mean of all elements */
export function mean(a: Float32Array): number {
  return a.length > 0 ? sum(a) / a.length : 0;
}

/** Variance */
export function variance(a: Float32Array): number {
  const m = mean(a);
  let v = 0;
  for (let i = 0; i < a.length; i++) { const d = a[i] - m; v += d * d; }
  return a.length > 0 ? v / a.length : 0;
}

// ─── Hamming Distance (for binary HDC) ───────────────────────────────────────

/** Hamming distance between two binary vectors */
export function hamming(a: Uint8Array, b: Uint8Array): number {
  let d = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) d++;
  return d;
}

/** XOR of two binary vectors */
export function xor(a: Uint8Array, b: Uint8Array, out?: Uint8Array): Uint8Array {
  const n = a.length;
  const r = out ?? new Uint8Array(n);
  for (let i = 0; i < n; i++) r[i] = a[i] ^ b[i];
  return r;
}

/** Majority vote bundle of binary vectors */
export function bundle(vectors: Uint8Array[]): Uint8Array {
  if (vectors.length === 0) return new Uint8Array(0);
  const n = vectors[0].length;
  const counts = new Int32Array(n);
  for (const v of vectors) {
    for (let i = 0; i < n; i++) counts[i] += v[i];
  }
  const threshold = vectors.length / 2;
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = counts[i] > threshold ? 1 : 0;
  return out;
}

/** Permute binary vector by shifting positions (for sequence encoding) */
export function permute(v: Uint8Array, shift: number): Uint8Array {
  const n = v.length;
  const out = new Uint8Array(n);
  const s = ((shift % n) + n) % n;
  for (let i = 0; i < n; i++) out[(i + s) % n] = v[i];
  return out;
}

// ─── Exponential Moving Average ──────────────────────────────────────────────

/** EMA update in-place: arr[i] = alpha * newVal[i] + (1-alpha) * arr[i] */
export function ema(arr: Float32Array, newVal: Float32Array, alpha: number): void {
  const beta = 1 - alpha;
  for (let i = 0; i < arr.length; i++) arr[i] = alpha * newVal[i] + beta * arr[i];
}

// ─── Max Pool / 1D Convolution helpers ───────────────────────────────────────

/** 1D max-pool over an array of feature maps */
export function maxPool1D(features: Float32Array, numFilters: number, seqLen: number): Float32Array {
  const out = new Float32Array(numFilters);
  for (let f = 0; f < numFilters; f++) {
    let best = -Infinity;
    for (let t = 0; t < seqLen; t++) {
      const val = features[t * numFilters + f];
      if (val > best) best = val;
    }
    out[f] = best > -Infinity ? best : 0;
  }
  return out;
}

/**
 * 1D convolution: apply `numFilters` kernels of width `kernelW` over input.
 * Input shape: [seqLen × inputDim], Kernel shape: [numFilters × kernelW × inputDim]
 * Bias shape: [numFilters]
 * Output shape: [(seqLen - kernelW + 1) × numFilters]
 */
export function conv1D(
  input: Float32Array, seqLen: number, inputDim: number,
  kernel: Float32Array, kernelW: number, numFilters: number,
  bias: Float32Array,
  out?: Float32Array
): Float32Array {
  const outLen = seqLen - kernelW + 1;
  const r = out ?? new Float32Array(outLen * numFilters);
  for (let t = 0; t < outLen; t++) {
    for (let f = 0; f < numFilters; f++) {
      let sum = bias[f];
      for (let k = 0; k < kernelW; k++) {
        for (let d = 0; d < inputDim; d++) {
          sum += input[(t + k) * inputDim + d] * kernel[(f * kernelW + k) * inputDim + d];
        }
      }
      r[t * numFilters + f] = sum;
    }
  }
  return r;
}

// ─── Dense Layer ─────────────────────────────────────────────────────────────

/** Dense layer: out = activation(W × input + bias) */
export function dense(
  input: Float32Array,
  W: Float32Array, rows: number, cols: number,
  bias: Float32Array,
  activation: (x: Float32Array) => Float32Array = vtanh,
  out?: Float32Array,
): Float32Array {
  const pre = matvec(W, rows, cols, input, out);
  vadd(pre, bias, pre);
  return activation(pre);
}

// ─── Serialization ───────────────────────────────────────────────────────────

/** Serialize Float32Array to base64 string */
export function f32ToBase64(arr: Float32Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str);
}

/** Deserialize base64 string to Float32Array */
export function base64ToF32(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Float32Array(bytes.buffer);
}

/** Serialize Uint8Array to base64 */
export function u8ToBase64(arr: Uint8Array): string {
  let str = "";
  for (let i = 0; i < arr.length; i++) str += String.fromCharCode(arr[i]);
  return btoa(str);
}

/** Deserialize base64 to Uint8Array */
export function base64ToU8(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
